import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApprovalQueue } from './components/ApprovalQueue';
import { PolicyEditor } from './components/PolicyEditor';
import { AuditTimeline } from './components/AuditTimeline';
import { ChatInterface } from './components/ChatInterface';
import { MonitoringPanel } from './components/MonitoringPanel';
import { TaskManager } from './components/TaskManager';
import { SetupWizard } from './components/SetupWizard';
import { LandingPage } from './components/LandingPage';
import {
  PrivacyPolicyPage,
  TermsPage,
  SecurityPage,
  AboutPage,
  BlogPage,
  CareersPage,
  ContactPage,
} from './components/SitePages';
import { Reports } from './components/Reports';
import { ConnectorSetup } from './components/ConnectorSetup';
import { SkillsPanel } from './components/SkillsPanel';
import { SimpleMode } from './components/SimpleMode';
import { GuidedTour } from './components/GuidedTour';
import { AdminPanel } from './components/AdminPanel';
import {
  MessageSquare,
  CheckSquare,
  FileText,
  History,
  Settings,
  Activity,
  Radio,
  BarChart3,
  Target,
  Plug,
  Brain,
  LayoutDashboard,
  ShieldCheck,
  HelpCircle,
} from 'lucide-react';
import logoImage from './assets/logo.png';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type TabType =
  | 'chat'
  | 'monitoring'
  | 'tasks'
  | 'approvals'
  | 'policies'
  | 'audit'
  | 'reports'
  | 'connectors'
  | 'skills'
  | 'admin';

interface NavTabProps {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  activeTab: TabType;
  onClick: (tab: TabType) => void;
}

function NavTab({ id, label, icon, activeTab, onClick }: NavTabProps) {
  const isActive = activeTab === id;
  return (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
        isActive
          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

interface SystemStatusProps {
  status: 'online' | 'offline' | 'connecting';
}

function SystemStatus({ status }: SystemStatusProps) {
  const statusConfig = {
    online: { label: 'Online', dotClass: 'status-online', textClass: 'text-success-400' },
    offline: { label: 'Offline', dotClass: 'status-offline', textClass: 'text-danger-400' },
    connecting: {
      label: 'Connecting...',
      dotClass: 'status-connecting',
      textClass: 'text-warning-400',
    },
  };

  const config = statusConfig[status];

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-lg">
      <Activity className="w-4 h-4 text-slate-400" />
      <span className="text-xs text-slate-400">System:</span>
      <span className={config.dotClass}></span>
      <span className={`text-xs font-medium ${config.textClass}`}>{config.label}</span>
    </div>
  );
}

export function App() {
  const [activeTab, setActiveTab] = React.useState<TabType>('chat');
  const [showSetupWizard, setShowSetupWizard] = React.useState(false);
  const [showLanding, setShowLanding] = React.useState(() => {
    try { return !localStorage.getItem('zoroagent_visited'); } catch { return true; }
  });
  const [systemStatus] = React.useState<'online' | 'offline' | 'connecting'>('online');
  const [showGuidedTour, setShowGuidedTour] = React.useState(false);
  const [simpleMode, setSimpleMode] = React.useState(false);

  React.useEffect(() => {
    try {
      const hasVisited = localStorage.getItem('zoroagent_visited');
      const hasCompletedSetup = localStorage.getItem('zoroagent_setup_complete');

      // If user has visited before but hasn't completed setup, show wizard
      if (hasVisited && !hasCompletedSetup) {
        setShowSetupWizard(true);
      }

      // Check if tour should auto-show for first-time users
      const tourComplete = localStorage.getItem('zoroagent_tour_complete');
      if (hasCompletedSetup && !tourComplete) {
        setShowGuidedTour(true);
      }
    } catch { /* Private browsing */ }
  }, []);

  // Hash-based routing for site pages
  const [currentPage, setCurrentPage] = React.useState(window.location.hash.slice(1) || '');
  React.useEffect(() => {
    const handleHashChange = () => setCurrentPage(window.location.hash.slice(1) || '');
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleGetStarted = () => {
    setShowLanding(false);
    try {
      localStorage.setItem('zoroagent_visited', 'true');
      // If setup not done, show setup
      const hasCompletedSetup = localStorage.getItem('zoroagent_setup_complete');
      if (!hasCompletedSetup) {
        setShowSetupWizard(true);
      }
    } catch { setShowSetupWizard(true); }
  };

  const handleSetupComplete = () => {
    try { localStorage.setItem('zoroagent_setup_complete', 'true'); } catch {}
    setShowSetupWizard(false);
  };

  // Route to site pages first
  const pageMap: Record<string, React.ReactNode> = {
    privacy: <PrivacyPolicyPage />,
    terms: <TermsPage />,
    security: <SecurityPage />,
    about: <AboutPage />,
    blog: <BlogPage />,
    careers: <CareersPage />,
    contact: <ContactPage />,
  };
  if (currentPage && pageMap[currentPage]) {
    return pageMap[currentPage] as JSX.Element;
  }

  if (showLanding) {
    return <LandingPage onGetStarted={handleGetStarted} />;
  }

  if (simpleMode) {
    return (
      <QueryClientProvider client={queryClient}>
        <SimpleMode onSwitchToAdvanced={() => setSimpleMode(false)} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="relative bg-slate-900 min-h-screen">
        {showSetupWizard && <SetupWizard onComplete={handleSetupComplete} />}
        <GuidedTour
          isOpen={showGuidedTour}
          onClose={() => setShowGuidedTour(false)}
          onStepChange={step => {
            const tabMap: TabType[] = [
              'chat',
              'monitoring',
              'tasks',
              'approvals',
              'policies',
              'audit',
              'reports',
            ];
            if (step < tabMap.length) setActiveTab(tabMap[step]);
          }}
        />
        <div
          className={`transition-all duration-700 ${showSetupWizard ? 'blur-md scale-95 opacity-50' : 'blur-0 scale-100 opacity-100'}`}
        >
          <AppContent
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            systemStatus={systemStatus}
            setShowSetupWizard={setShowSetupWizard}
            setSimpleMode={setSimpleMode}
            setShowGuidedTour={setShowGuidedTour}
          />
        </div>
      </div>
    </QueryClientProvider>
  );
}

// Extracted AppContent for cleaner Main render
function AppContent({
  activeTab,
  setActiveTab,
  systemStatus,
  setShowSetupWizard,
  setSimpleMode,
  setShowGuidedTour,
}: any) {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="glass border-b border-slate-700/50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25 overflow-hidden">
                <img src={logoImage} alt="ZoroAgent Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Zoro</h1>
                <p className="text-xs text-slate-400">Autonomous Security Engineer</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <SystemStatus status={systemStatus} />

              <button
                onClick={() => setSimpleMode(true)}
                className="btn btn-ghost p-2"
                title="Simple Mode"
              >
                <LayoutDashboard className="w-5 h-5" />
              </button>

              <button
                onClick={() => setShowGuidedTour(true)}
                className="btn btn-ghost p-2"
                title="Guided Tour"
              >
                <HelpCircle className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 pl-4 border-l border-slate-700">
                <div className="text-right">
                  <span className="text-sm text-slate-300 block">Agent: EMP-AI-001</span>
                  <span className="text-xs text-slate-500">v1.0.0</span>
                </div>
                <span className="badge badge-success">On Duty</span>
              </div>

              <button
                onClick={() => setShowSetupWizard(true)}
                className="btn btn-ghost p-2"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="glass border-b border-slate-700/50">
        <div className="container mx-auto px-6 py-2">
          <div className="flex gap-2 overflow-x-auto">
            <NavTab
              id="chat"
              label="Chat"
              icon={<MessageSquare className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="monitoring"
              label="Monitoring"
              icon={<Radio className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="tasks"
              label="Tasks"
              icon={<Target className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="approvals"
              label="Approvals"
              icon={<CheckSquare className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="policies"
              label="Policies"
              icon={<FileText className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="connectors"
              label="Connectors"
              icon={<Plug className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="skills"
              label="Skills"
              icon={<Brain className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="audit"
              label="Audit"
              icon={<History className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="reports"
              label="Reports"
              icon={<BarChart3 className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <NavTab
              id="admin"
              label="Admin"
              icon={<ShieldCheck className="w-4 h-4" />}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-6">
        <div className="animate-fade-in">
          {activeTab === 'chat' && <ChatInterface />}
          {activeTab === 'monitoring' && <MonitoringPanel />}
          {activeTab === 'tasks' && <TaskManager />}
          {activeTab === 'approvals' && <ApprovalQueue />}
          {activeTab === 'policies' && <PolicyEditor />}
          {activeTab === 'connectors' && <ConnectorSetup />}
          {activeTab === 'skills' && <SkillsPanel />}
          {activeTab === 'audit' && <AuditTimeline />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'admin' && <AdminPanel />}
        </div>
      </main>
    </div>
  );
}
